import cn from 'classnames'
import BaseModal, {
  BaseModalProps,
} from 'ext-ui/components/BaseModal/BaseModal'
import Button from 'ext-ui/components/Button/Button'
import SingleDropdown from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import NotesInputField from 'ext-ui/components/NotesInputField/NotesInputField'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import { white100 } from 'ext-ui/style/colors'
import React, { ReactElement } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import SvgFlagNew from 'src/components/Svgs/SvgFlagNew'
import Tag from 'src/components/Tag/Tag'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes } from 'src/enums'
import MerchantIcon from 'src/feature/MerchantIcon/MerchantIcon'
import { useFormFields } from 'src/hooks'
import { dateFormatString } from 'src/localization/Dte'
import { getLocaleInfo } from 'src/localization/locales'
import { useTranslate } from 'src/localization/Trns'
import {
  TransactionManagerRejectionReason,
  transactionManagerReviewRejectionReasonToString,
  TransactionManagerReviewStatus,
} from 'src/models/transaction.models'
import { usePatchTransactionManagerReview } from 'src/queries/expenseReview.queries'
import {
  activeAlphabeticalExpenseCategoryFiltersRequestParams,
  useQueryExpenseCategories,
} from 'src/queries/organizations/expenseCategories.queries'
import {
  useQueryTransactionDetail,
  useQueryTransactionsCount,
  useQueryTransactionsPerson,
} from 'src/queries/transactions.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { stringIsEmpty } from 'src/utils/validation/string.validation'

import style from './ManagerReviewRejectionModal.style'

type ManagerReviewRejectionModalProps = Omit<BaseModalProps, 'variant'> & {
  transactionIds: string[]
  filters?: TransactionsFiltersBody
}

function ManagerReviewRejectionModal(
  props: ManagerReviewRejectionModalProps,
): ReactElement {
  const {
    className = '',
    isOpen,
    onClose: onModalClose,
    transactionIds,
    filters,
  } = props
  const { analytics } = useAnalyticsContext()
  const translate = useTranslate()
  const locale = useSelector(selectNullSafeLocaleFromState)

  const { data: transaction } = useQueryTransactionDetail({
    transactionId: transactionIds[0],
    enabled: transactionIds.length === 1,
  })

  const { data: cardUser } = useQueryTransactionsPerson(
    transaction?.recipientId,
  )

  const { mutate: patchManagerReviewRejection, isPending } =
    usePatchTransactionManagerReview()

  const {
    fields,
    setFields,
    fieldsErrors,
    setFieldsErrors,
    onFieldChange,
    setErrorsAndReturnDidPassValidation,
  } = useFormFields<{
    managerRejectionReason?: TransactionManagerRejectionReason
    managerRejectionNote?: string
  }>({}, () => ({
    managerRejectionReason: [stringIsEmpty()],
  }))

  // Fetch expense categories to determine if we should show exp cat related rejection reasons
  const { data: expCatSchema } = useQueryExpenseCategories({
    filters: activeAlphabeticalExpenseCategoryFiltersRequestParams,
  })

  const reasonOptionGroups = [
    {
      id: 'spender-action-required',
      heading: translate('Require Spender Resolution'),
      options: [
        {
          value: TransactionManagerRejectionReason.MissingReceipt,
          label: translate(
            transactionManagerReviewRejectionReasonToString(
              TransactionManagerRejectionReason.MissingReceipt,
            ),
          ),
          selected:
            fields['managerRejectionReason'] ===
            TransactionManagerRejectionReason.MissingReceipt,
        },
        {
          value: TransactionManagerRejectionReason.IncorrectReceipt,
          label: translate(
            transactionManagerReviewRejectionReasonToString(
              TransactionManagerRejectionReason.IncorrectReceipt,
            ),
          ),
          selected:
            fields['managerRejectionReason'] ===
            TransactionManagerRejectionReason.IncorrectReceipt,
        },
        ...(expCatSchema && expCatSchema.length > 0
          ? [
              {
                value:
                  TransactionManagerRejectionReason.MissingExpenseCategories,
                label: translate(
                  transactionManagerReviewRejectionReasonToString(
                    TransactionManagerRejectionReason.MissingExpenseCategories,
                  ),
                ),
                selected:
                  fields['managerRejectionReason'] ===
                  TransactionManagerRejectionReason.MissingExpenseCategories,
              },
              {
                value:
                  TransactionManagerRejectionReason.IncorrectExpenseCategories,
                label: translate(
                  transactionManagerReviewRejectionReasonToString(
                    TransactionManagerRejectionReason.IncorrectExpenseCategories,
                  ),
                ),
                selected:
                  fields['managerRejectionReason'] ===
                  TransactionManagerRejectionReason.IncorrectExpenseCategories,
              },
            ]
          : []),
        {
          value: TransactionManagerRejectionReason.MissingNote,
          label: translate(
            transactionManagerReviewRejectionReasonToString(
              TransactionManagerRejectionReason.MissingNote,
            ),
          ),
          selected:
            fields['managerRejectionReason'] ===
            TransactionManagerRejectionReason.MissingNote,
        },
      ],
    },
    {
      id: 'no-spender-action-required',
      heading: translate('Notify Admins'),
      options: [
        {
          value: TransactionManagerRejectionReason.OutOfPolicy,
          label: translate(
            transactionManagerReviewRejectionReasonToString(
              TransactionManagerRejectionReason.OutOfPolicy,
            ),
          ),
          selected:
            fields['managerRejectionReason'] ===
            TransactionManagerRejectionReason.OutOfPolicy,
        },
        {
          value: TransactionManagerRejectionReason.Other,
          label: translate(
            transactionManagerReviewRejectionReasonToString(
              TransactionManagerRejectionReason.Other,
            ),
          ),
          selected:
            fields['managerRejectionReason'] ===
            TransactionManagerRejectionReason.Other,
        },
      ],
    },
  ]

  const submit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    if (!setErrorsAndReturnDidPassValidation()) return

    patchManagerReviewRejection(
      {
        body: {
          transactionIds: transactionIds,
          managerReviewStatus: TransactionManagerReviewStatus.Rejected,
          managerRejectionReason: fields['managerRejectionReason'],
          managerRejectionNote: fields['managerRejectionNote'],
        },
        filters: props.filters || {},
      },
      {
        onSuccess: onClose,
      },
    )

    analytics?.track('manager review actioned txn', {
      action: transactionIds.length === 1 ? 'rejected' : 'bulk rejected',
    })
  }

  const { data: allTransactionsCount } = useQueryTransactionsCount({
    filters,
  })

  const onClose = (): void => {
    // reset form fields and errors
    setFields({})
    setFieldsErrors({})
    // close modal
    onModalClose()
  }

  return (
    <BaseModal
      className={cn('ManagerReviewRejectionModal', className)}
      aria-label={translate('Reject Transactions Modal')}
      variant={'interactive'}
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton
      css={style}
    >
      <form onSubmit={submit} className={'flex-column gap-10'}>
        <div className={'flex-column gap-4'}>
          <div className={'font-h2 text-primary'}>
            {translate(
              transactionIds.length > 1
                ? 'Reject ${count} Transactions'
                : 'Reject Transaction',
              {
                count:
                  transactionIds.length > 0
                    ? transactionIds.length.toString()
                    : allTransactionsCount?.toString() || '',
              },
            )}
          </div>
          <div className={'font-md text-primary'}>
            {translate(
              transactionIds.length > 1
                ? 'Provide a reason for rejecting these transactions. Spenders will be notified when a rejection requires them to correct or modify the receipt, category, or note. Admins will be notified when a rejection is due to policy or other circumstances.'
                : 'Provide a reason for rejecting this transaction. Spenders will be notified when a rejection requires them to correct or modify the receipt, category, or note. Admins will be notified when a rejection is due to policy or other circumstances.',
            )}
          </div>
        </div>
        <div className={'flex-column gap-6'}>
          {/* Hide transaction details when multiple transactions are selected */}
          {transaction && transactionIds.length === 1 && (
            <div className={'transactionDetails'}>
              <div className={'transactionDetails-detailItem'}>
                <div className={'font-md text-secondary'}>
                  {translate('Merchant')}
                </div>
                <div
                  className={'font-md text-primary flex-row gap-2 align-center'}
                >
                  <MerchantIcon
                    size={24}
                    merchantImgSrc={transaction.merchantImageSrc('medium')}
                    mccGroupKey={transaction.mccGroupKey ?? 'OTHER'}
                  />
                  {transaction.normalizedMerchantNameOrUnknown}
                  {transaction.normalizedMerchant && (
                    <TooltipIcon>
                      {translate('Original Merchant: ${merchantName}', {
                        merchantName:
                          transaction.merchantName ??
                          translate('Unknown Merchant'),
                      })}
                    </TooltipIcon>
                  )}
                </div>
              </div>
              <div className={'transactionDetails-detailItem'}>
                <div className={'font-md text-secondary'}>
                  {translate('Card User')}
                </div>
                <div
                  className={'font-md text-primary flex-row gap-2 align-center'}
                >
                  {cardUser && (
                    <UserAvatar user={cardUser} size={AvatarSizes.Small24} />
                  )}
                  {transaction.recipientName}
                </div>
              </div>
              <div className={'transactionDetails-detailItem'}>
                <div className={'font-md text-secondary'}>
                  {translate('Amount')}
                </div>
                <div
                  className={
                    'font-md text-primary flex-row gap-1 align-baseline'
                  }
                >
                  {transaction.clearingOrAuthLabel(true)}
                  {transaction.currency !== getLocaleInfo(locale).currency && (
                    <div className={'font-sm text-secondary'}>
                      {transaction.currency}
                    </div>
                  )}
                </div>
              </div>
              <div className={'transactionDetails-detailItem'}>
                <div className={'font-md text-secondary'}>
                  {translate('Card')}
                </div>
                <div
                  className={'font-md text-primary flex-row gap-1 align-center'}
                >
                  {transaction.vcnDisplayName}
                  <Tag fontSize={10} text={`${transaction.vcnLast4}`} />
                </div>
              </div>
              <div className={'transactionDetails-detailItem'}>
                <div className={'font-md text-secondary'}>
                  {translate('Date')}
                </div>
                <div className={'font-md text-primary'}>
                  {/* This modal only appears from a list of cleared txns so clearedAt is never undefined */}
                  {dateFormatString(transaction?.clearedAt || '', 'l')}
                </div>
              </div>
            </div>
          )}
          <SingleDropdown
            data-testid={'reason'}
            label={translate('Reason')}
            optionGroups={reasonOptionGroups}
            noneLabel={''}
            selectedValue={
              fields['managerRejectionReason'] !== undefined
                ? {
                    value: fields['managerRejectionReason'],
                    label: reasonOptionGroups
                      .flatMap((group) => group.options)
                      .find(
                        (option) =>
                          option.value === fields['managerRejectionReason'],
                      )?.label,
                  }
                : undefined
            }
            onSelect={({ value }) => {
              onFieldChange('managerRejectionReason', value)
              setFieldsErrors({
                ...fieldsErrors,
                managerRejectionReason: undefined,
              })
            }}
            placeholder={translate('Select reason for rejecting...')}
            errorText={fieldsErrors['managerRejectionReason']}
            requirement={'required'}
          />
          <div className={'flex-column gap-2'}>
            <span className={'font-form-label'}>{translate('Note')}</span>
            <NotesInputField
              value={fields['managerRejectionNote']}
              onChange={(value) => onFieldChange('managerRejectionNote', value)}
              placeholder={translate(
                'Please explain what needs to be updated or why this transaction is being rejected...',
              )}
              id={props.id}
              maxLength={2000}
              notesCharacters={fields['managerRejectionNote']?.length || 0}
            />
          </div>
        </div>
        <div className={'flex-row gap-6 align-center justify-end'}>
          <Button
            variant={'no-shell'}
            dropShadow={false}
            aria-label={translate('Cancel')}
            onClick={onClose}
            data-testid={'cancelBtn'}
          >
            {translate('Cancel')}
          </Button>
          <Button
            variant={'severe'}
            type={'submit'}
            iconLeft={<SvgFlagNew width={18} height={18} stroke={white100} />}
            aria-label={translate('Reject')}
            loading={isPending}
            data-testid={'rejectTransactionBtn'}
          >
            {translate('Reject')}
          </Button>
        </div>
      </form>
    </BaseModal>
  )
}

export default React.memo(ManagerReviewRejectionModal)
